import React from 'react';
import { button, card, MetadataPreview } from './editors';
import type { Detail } from './model';
import type { BookIntent } from './publication-visibility';

/** A completed/waiting request is history, not another publication decision. */
export function SubmissionOutcome({
  detail,
  reload,
  openBook,
}: {
  detail: Detail;
  reload(): void;
  openBook?(id: string, intent?: BookIntent): void;
}) {
  const { submission, currentBook } = detail;
  const published = submission.status === 'published';
  const targetId = published
    ? submission.publishedBookId
    : submission.status === 'matched'
      ? submission.matchedBookId
      : null;
  const publishedBook = published && currentBook?.id === targetId ? currentBook : null;
  const hidden = publishedBook?.catalogueVisibility === 'hidden';
  const heading = {
    published: hidden ? 'Removed from the public catalogue' : 'Published in catalogue',
    matched: 'Awaiting reader confirmation',
    changes_requested: 'Awaiting reader changes',
    declined: 'Publication declined',
    withdrawn: 'Request withdrawn',
    pending: 'Publication request',
  }[submission.status];
  const description = {
    published: hidden
      ? 'This publication has been removed from public discovery. You can edit it or restore its visibility.'
      : 'You can edit this published edition or remove it from the public catalogue. Changes apply to the shared book.',
    matched: 'The proposed edition is waiting for the reader to accept the match.',
    changes_requested:
      'The reader needs to revise and resubmit before another moderation decision.',
    declined: 'This request has been declined. A new submission is needed for another review.',
    withdrawn: 'The reader withdrew this request.',
    pending: '',
  }[submission.status];
  return (
    <section className={card} aria-label="Publication request outcome">
      <p className="admin-eyebrow">Publication request</p>
      <h2 className="mt-3 break-words font-serif text-3xl">{submission.metadata.title}</h2>
      <div className="mt-5 rounded-xl bg-thread-soft p-5">
        <h3 className="font-serif text-2xl">{heading}</h3>
        <p className="mt-2 text-sm leading-6 text-ink-soft">{description}</p>
        {targetId && openBook && (
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              className={published ? 'admin-button-primary' : button}
              onClick={() => openBook(targetId, 'edit')}
            >
              {published ? 'Edit publication' : 'Inspect proposed edition'}
            </button>
            {published && (
              <button
                type="button"
                className={hidden ? button : 'admin-button-danger'}
                onClick={() => openBook(targetId, 'visibility')}
              >
                {hidden ? 'Restore publication' : 'Remove publication'}
              </button>
            )}
          </div>
        )}
      </div>
      {submission.moderatorMessage && (
        <p className="mt-5 text-sm leading-6">{submission.moderatorMessage}</p>
      )}
      {publishedBook && (
        <>
          <p className="mt-5 text-sm font-semibold">Current catalogue edition</p>
          <MetadataPreview value={publishedBook} />
        </>
      )}
      <details className="mt-6 border-t border-line/65 pt-5">
        <summary className="text-sm font-semibold">Submitted snapshot</summary>
        <MetadataPreview value={submission.metadata} />
      </details>
      <footer className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line/65 pt-5">
        <p className="text-xs text-ink-muted">
          Version {submission.version} · Submitted{' '}
          {new Date(submission.createdAt).toLocaleDateString()}
        </p>
        <button type="button" className={button} onClick={reload}>
          Reload request
        </button>
      </footer>
    </section>
  );
}
